import _ from 'lodash';
/** import third-party libraries in the section above, keep the ascending order */

import { can } from 'services/can';
import { createHttp } from 'utils/http';
import { PermissionsMapping } from 'common/constants/role-intents';
import {
    filterRecordsByKeywords,
    sortRecordsByName,
} from 'states/resources/organizations/business-logic';
/** import classes and others in the section above, keep the ascending order */

const http = createHttp();

/**
 * Loads child organizations via GraphQL `organizations` (urban `@authorize(admin_subOrg__view)`).
 * Uses one query document; nested `users` is included only when `includeUsers` is true.
 * Selection set is minimal — see graphql-minimal-fields convention.
 * Includes nested `relationshipToAuthOrganization` (no post-fetch flatten) so the
 * sidebar can gate Edit/Deprecate on `_relationship === 'child'`.
 *
 * @param {Object} options - Fetch options
 * @param {Object} options.organizationFilters - `OrganizationFilters` (e.g. `{ relationship: 'child', type }`, `{ organizationIds: [id] }`)
 * @param {Object} options.userFilters - `UserFilters` for nested `users` (always passed; used when `includeUsers` is true)
 * @param {boolean} [options.includeUsers] - When false, omits nested `users` selection (callers without `admin_user__view`)
 * @returns {Promise<Array<Object>>} Organizations with `id`, `name`, `type`, `client_type`, `relationshipToAuthOrganization`, and `users` when requested
 */
async function fetchOrganizations({ organizationFilters, userFilters, includeUsers }) {
    const query = `
        query getChildOrganizationsByType($organizationFilters: OrganizationFilters${
            includeUsers ? `, $userFilters: UserFilters` : ``
        }) {
            organizations(filters: $organizationFilters) {
                id
                name
                type
                client_type
                relationshipToAuthOrganization {
                    _relationship
                }
                ${
                    includeUsers
                        ? `users(filters: $userFilters) {
                        id
                        name
                        email
                        global_role
                        organization
                        org {
                            name
                        }
                    }`
                        : ``
                }
            }
        }
    `;

    const variables = includeUsers ? { organizationFilters, userFilters } : { organizationFilters };

    const { organizations } = await http.graphql(query, variables);

    if (!Array.isArray(organizations)) {
        return [];
    }

    return organizations;
}

const actions = {
    init: () => {
        return async dispatch => {
            dispatch({
                type: 'ADMIN_V2__CHILD_ORGANIZATION_DASH_INIT_START',
            });
            await dispatch(actions.fetchAllOrganizationsByType());
            dispatch({
                type: 'ADMIN_V2__CHILD_ORGANIZATION_DASH_INIT_END',
            });
        };
    },
    fetchOrganizationData: orgId => {
        return async (dispatch, getState) => {
            try {
                dispatch({
                    type: 'ADMIN_V2__FETCH_CHILD_ORGANIZATION_START',
                    payload: {},
                });

                const organizations = await fetchOrganizations({
                    organizationFilters: {
                        organizationIds: [orgId],
                        deprecated: false,
                    },
                    userFilters: { deprecated: false },
                    includeUsers: true,
                });
                const organizationRaw = organizations[0] ?? null;

                let sortedUsers = [];
                if (organizationRaw) {
                    sortedUsers = sortRecordsByName(organizationRaw.users, {
                        nameField: 'name',
                    });
                }

                const organization = organizationRaw
                    ? { ...organizationRaw, users: sortedUsers }
                    : null;

                const childOrgState = _.get(getState(), 'adminV2.childOrganization', {});
                let usersByOrgId = childOrgState.usersByOrgId ?? {};
                let allUsersFlattened = childOrgState.allUsersFlattened ?? [];

                if (organization) {
                    usersByOrgId = {
                        ...usersByOrgId,
                        [organization.id]: organization.users,
                    };
                    allUsersFlattened = sortRecordsByName(Object.values(usersByOrgId).flat(), {
                        nameField: 'name',
                    });
                }

                dispatch({
                    type: 'ADMIN_V2__FETCH_CHILD_ORGANIZATION_END',
                    payload: {
                        data: {
                            organization,
                        },
                        usersByOrgId,
                        allUsersFlattened,
                    },
                });
            } catch (err) {
                if (window.bugsnagClient) {
                    window.bugsnagClient.notify(
                        `Failed to fetch organization user data in admin page`,
                        {
                            metaData: {
                                organizationId: _.get(getState(), 'profile.organizationId'),
                            },
                        }
                    );
                }
            }
        };
    },

    fetchAllOrganizationsByType: (type = 'advertiser') => {
        return async (dispatch, getState) => {
            dispatch({
                type: 'ADMIN_V2__FETCH_CHILD_ORGS_BY_TYPE_START',
            });

            const typeQuery =
                type === 'media_buyer' || type === 'admin'
                    ? { type: [type] }
                    : { client_type: [type] };
            const organizationFilters = { ...typeQuery, relationship: 'child', deprecated: false };
            const profileOrganizationId = _.get(getState(), 'profile.organizationId');
            // admin_user__view — gates nested users in bulk GraphQL (urban GET /users)
            const includeUsers = can(PermissionsMapping.ADMIN_USER__VIEW);

            const organizationsAndSelfOrg = await fetchOrganizations({
                organizationFilters,
                userFilters: { deprecated: false },
                includeUsers,
            });

            const ownOrganization = _.find(
                organizationsAndSelfOrg,
                org => org.id === profileOrganizationId
            );
            const filteredOrganizations = _(organizationsAndSelfOrg)
                .filter(org => org.id !== profileOrganizationId)
                .value();

            // Sort using locale-aware comparison to handle accented characters properly
            const organizations = sortRecordsByName(filteredOrganizations);

            let users = [];
            let allUsersFlattened = [];
            let usersByOrgId = {};

            if (includeUsers) {
                // usersByOrgId: per-org users from bulk load; sidebar must not re-query
                usersByOrgId = {};
                for (const organization of organizations) {
                    const orgUsers = Array.isArray(organization.users) ? organization.users : [];
                    usersByOrgId[organization.id] = sortRecordsByName(orgUsers, {
                        nameField: 'name',
                    });
                }

                const rawUsers = _(organizations)
                    .filter(
                        organization =>
                            Array.isArray(organization.users) && organization.users.length > 0
                    )
                    .map(organization => organization.users)
                    .flatten()
                    .value();

                // allUsersFlattened carries org.name for the "All" table column
                allUsersFlattened = sortRecordsByName(rawUsers, { nameField: 'name' });
                users = allUsersFlattened;
            }

            dispatch({
                type: 'ADMIN_V2__FETCH_CHILD_ORGS_BY_TYPE_END',
                payload: {
                    users,
                    allUsersFlattened,
                    usersByOrgId,
                    organizations,
                    ownOrganizationType: _.get(ownOrganization, 'type'),
                    // 'all' only when users panel exists; null avoids phantom "All …" selection
                    currentOrganization: includeUsers ? 'all' : null,
                },
            });
        };
    },

    /**
     * Select the "All" users view from bulk-loaded cache (no network).
     */
    selectAllOrganizations: () => {
        return (dispatch, getState) => {
            const childOrgState = _.get(getState(), 'adminV2.childOrganization', {});
            const allUsersFlattened = childOrgState.allUsersFlattened ?? [];
            dispatch({
                type: 'ADMIN_V2__SELECT_ALL_ORGANIZATIONS',
                payload: { users: allUsersFlattened },
            });
        };
    },

    /**
     * Select one org's users from bulk-loaded cache; falls back to network on cache miss.
     *
     * @param {string} orgId
     */
    selectOrganization: orgId => {
        return (dispatch, getState) => {
            const childOrgState = _.get(getState(), 'adminV2.childOrganization', {});
            const usersByOrgId = childOrgState.usersByOrgId ?? {};
            const organizationsForList = childOrgState.organizationsForList ?? [];
            const orgMeta = organizationsForList.find(organization => organization.id === orgId);
            const hasCachedUsers = Object.prototype.hasOwnProperty.call(usersByOrgId, orgId);

            if (orgMeta && hasCachedUsers) {
                const users = usersByOrgId[orgId];
                dispatch({
                    type: 'ADMIN_V2__SELECT_ORGANIZATION',
                    payload: {
                        organization: { ...orgMeta, users },
                        users,
                    },
                });
                return;
            }

            // Cache miss (e.g. org just created) — intentional network fallback
            return dispatch(actions.fetchOrganizationData(orgId));
        };
    },

    /**
     * After org save: select org and users in the table without a network round-trip.
     *
     * @param {Object} org - Saved organization metadata
     * @param {Array<Object>} users - Dashboard users for the org (may be empty)
     */
    applySavedOrganization: (org, users) => {
        return (dispatch, getState) => {
            const childOrgState = _.get(getState(), 'adminV2.childOrganization', {});
            const usersByOrgId = {
                ...(childOrgState.usersByOrgId ?? {}),
                [org.id]: users,
            };
            const allUsersFlattened = sortRecordsByName(Object.values(usersByOrgId).flat(), {
                nameField: 'name',
            });
            dispatch({
                type: 'ADMIN_V2__APPLY_SAVED_ORGANIZATION',
                payload: { org, users, usersByOrgId, allUsersFlattened },
            });
        };
    },

    /**
     * Upsert or remove a user in the child-org cache without a network round-trip.
     * Avoids page-level BlockLoadGroup spinner after AdminUserForm / delete-user-modal.
     *
     * Delete marker shape (from deleteModalActions): `{ id, organization, _deleted: true }`.
     * Create/update: API user; `organization` selects the single `usersByOrgId` bucket to update.
     *
     * @param {Object} user - API-returned user, or delete marker with id + organization
     */
    applySavedUser: user => {
        return (dispatch, getState) => {
            if (!user || !user.id) {
                return;
            }

            const childOrgState = _.get(getState(), 'adminV2.childOrganization', {});
            const nextUsersByOrgId = { ...(childOrgState.usersByOrgId ?? {}) };
            // Both create and delete markers carry organization — do not scan every org bucket
            const orgId = user.organization;

            if (!orgId) {
                return;
            }

            let normalizedUser = user;

            if (user._deleted === true) {
                // Delete: prune one org list only (organization comes from the deleted user row)
                nextUsersByOrgId[orgId] = (nextUsersByOrgId[orgId] || []).filter(
                    existing => existing.id !== user.id
                );
            } else {
                // Create/update: ensure All-table columns have name + org.name without refetch
                let orgName = user.org?.name;
                if (!orgName) {
                    if (
                        childOrgState.currentOrganization !== null &&
                        childOrgState.currentOrganization !== 'all' &&
                        childOrgState.currentOrganization.id === orgId
                    ) {
                        orgName = childOrgState.currentOrganization.name;
                    } else {
                        const orgMeta = (childOrgState.organizationsForList || []).find(
                            organization => organization.id === orgId
                        );
                        orgName = orgMeta?.name;
                    }
                }

                const displayName =
                    user.name ||
                    [user.first_name, user.last_name]
                        .filter(Boolean)
                        .join(' ')
                        .trim() ||
                    '';

                normalizedUser = {
                    ...user,
                    name: displayName,
                    org: orgName ? { name: orgName } : user.org,
                };

                const orgUsers = [...(nextUsersByOrgId[orgId] || [])];
                const existingIndex = orgUsers.findIndex(
                    existing => existing.id === normalizedUser.id
                );
                if (existingIndex === -1) {
                    orgUsers.push(normalizedUser);
                } else {
                    orgUsers[existingIndex] = { ...orgUsers[existingIndex], ...normalizedUser };
                }
                nextUsersByOrgId[orgId] = orgUsers;
            }

            const allUsersFlattened = sortRecordsByName(Object.values(nextUsersByOrgId).flat(), {
                nameField: 'name',
            });

            dispatch({
                type: 'ADMIN_V2__APPLY_SAVED_USER',
                payload: {
                    user: normalizedUser,
                    usersByOrgId: nextUsersByOrgId,
                    allUsersFlattened,
                },
            });
        };
    },

    /**
     * Resolve users for a saved existing org from cache, or fetch when cache is empty.
     *
     * @param {string} orgId
     * @returns {Promise<Array<Object>>}
     */
    resolveUsersAfterOrgSave: orgId => {
        return async (dispatch, getState) => {
            const childOrgState = _.get(getState(), 'adminV2.childOrganization', {});
            const usersByOrgId = childOrgState.usersByOrgId ?? {};
            if (Object.prototype.hasOwnProperty.call(usersByOrgId, orgId)) {
                return usersByOrgId[orgId];
            }

            await dispatch(actions.fetchOrganizationData(orgId));
            const afterFetch = _.get(getState(), 'adminV2.childOrganization.usersByOrgId', {});
            return afterFetch[orgId] || [];
        };
    },

    changeOrgFilterType: value => {
        return async dispatch => {
            dispatch({
                type: 'ADMIN_V2__CHANGE_ORG_FILTER_TYPE_START',
            });
            await dispatch(actions.fetchAllOrganizationsByType(value));
            dispatch({
                type: 'ADMIN_V2__CHANGE_ORG_FILTER_TYPE_END',
            });
        };
    },

    filterOrganizationsByText: keywords => {
        return (dispatch, getState) => {
            dispatch({
                type: 'ADMIN_V2__FILTER_CHILD_ORGS_START',
            });
            const orgs = _.get(getState(), 'adminV2.childOrganization.organizationsForList');

            // Search for keywords in organization name and id (accent-insensitive)
            const filteredOrgs = filterRecordsByKeywords(orgs, keywords);

            dispatch({
                type: 'ADMIN_V2__FILTER_CHILD_ORGS_END',
                payload: { data: filteredOrgs },
            });
        };
    },

    /**
     * Insert or replace a child org in the sidebar lists without refetching all orgs.
     * New creates default `relationshipToAuthOrganization._relationship` to `'child'` so
     * Edit/Deprecate stay enabled before the next bulk fetch returns nested relationship data.
     *
     * @param {Object} org - Saved organization from create/update (must include id)
     */
    upsertChildOrganization: org => {
        return (dispatch, getState) => {
            if (!org || !org.id) {
                return;
            }

            const organizationsForList = _.get(
                getState(),
                'adminV2.childOrganization.organizationsForList',
                []
            );
            const existingIndex = organizationsForList.findIndex(
                existingOrg => existingOrg.id === org.id
            );
            const existingOrg = existingIndex === -1 ? null : organizationsForList[existingIndex];
            const listItem = {
                id: org.id,
                name: org.name,
                type: org.type,
                client_type: org.client_type,
                // Preserve existing nested relationship from bulk load; new orgs assume direct child.
                relationshipToAuthOrganization:
                    existingIndex === -1
                        ? { _relationship: 'child' }
                        : existingOrg.relationshipToAuthOrganization ??
                          org.relationshipToAuthOrganization ?? { _relationship: 'child' },
            };
            let nextList;
            if (existingIndex === -1) {
                nextList = [...organizationsForList, listItem];
            } else {
                nextList = organizationsForList.map((existingOrg, index) =>
                    index === existingIndex ? { ...existingOrg, ...listItem } : existingOrg
                );
            }

            dispatch({
                type: 'ADMIN_V2__UPSERT_CHILD_ORG',
                payload: {
                    organizations: sortRecordsByName(nextList),
                },
            });
        };
    },

    /**
     * Remove a deprecated child org and its users from local cache (no GraphQL refetch).
     * @param {string} orgId - Deprecated organization id
     */
    applyDeprecatedOrganization: orgId => {
        return {
            type: 'ADMIN_V2__DEPRECATE_CHILD_ORG',
            payload: { orgId },
        };
    },

    /**
     * Filters child-organization users by name, id, or email (@ substring or exact local/domain).
     * @param {string} keywords - Search text
     */
    filterUsers: keywords => {
        return (dispatch, getState) => {
            dispatch({
                type: 'ADMIN_V2__FITER_CHILD_USERS_START',
                payload: {},
            });
            const users = _.get(getState(), 'adminV2.childOrganization.users', []);
            // @ substring or exact local/domain email match (case-insensitive)
            const filteredUsers = filterRecordsByKeywords(users, keywords, {
                nameField: 'name',
                idField: 'id',
                emailField: 'email',
            });
            dispatch({
                type: 'ADMIN_V2__FITER_CHILD_USERS_END',
                payload: { data: filteredUsers },
            });
        };
    },
};

export default actions;
